// Story loading and rendering
import { 
  API_BASE, USER_TIMEZONE, stories, expandedStoryId, lastUpdated, currentCategory,
  currentLayout, sessionExpandCount, nudgeDismissed, isSignedIn, hasGivenFeedback,
  setStories, setExpandedStoryId, setLastUpdated, setSessionExpandCount, getStories
} from './state.js';
import { formatRelativeTime, escapeHtml } from './utils.js';
import { showFeedbackNudge } from './feedback.js';
import { isBookmarked } from './bookmarks.js';

export async function loadStories(category) {
  const storiesContainer = document.getElementById('stories-container');
  const footerMessage = document.getElementById('footer-message');
  
  storiesContainer.innerHTML = '<div class="loading">Loading stories</div>';
  footerMessage.style.display = 'none';

  try {
    const response = await fetch(`${API_BASE}/stories/${category}?tz=${encodeURIComponent(USER_TIMEZONE)}`);
    const data = await response.json();
    setStories(data.stories);
    setLastUpdated(data.lastUpdated);
    
    const savedExpandedId = expandedStoryId;
    setExpandedStoryId(null);
    if (savedExpandedId && data.stories.some(s => s.id === savedExpandedId)) {
      setExpandedStoryId(savedExpandedId);
    }
    renderStories();
  } catch (error) {
    console.error('Failed to load stories:', error);
    storiesContainer.innerHTML = '<div class="loading">Failed to load stories. Please try again.</div>';
  }
}

export function applyLayout() {
  const storiesContainer = document.getElementById('stories-container');
  storiesContainer.className = 'stories-container';
  if (currentLayout === 'list') {
    storiesContainer.classList.add('layout-list');
  }
}

export function renderStories() {
  const storiesContainer = document.getElementById('stories-container');
  const footerMessage = document.getElementById('footer-message');

  if (stories.length === 0) {
    storiesContainer.innerHTML = '<div class="loading">No stories found for this category.</div>';
    footerMessage.style.display = 'none';
    return;
  }

  applyLayout();

  storiesContainer.innerHTML = stories.map((story, index) => {
    const sourceCount = story.articles.length;
    const hasPaywall = story.hasPaywall || false;
    const bookmarked = isBookmarked(story.id);
    const shouldShowDeepDive = sourceCount >= 3;

    return `
    <article class="story-card ${expandedStoryId === story.id ? 'expanded' : ''}" data-story-id="${story.id}" onclick="window.appToggleStory('${story.id}')">
      <div class="story-main">
        <div class="story-header">
          <div class="story-header-top"><span class="story-number">${index + 1}</span></div>
          <div class="story-headline">
            <div class="story-meta-top">
              <span class="story-topic">${escapeHtml(story.topic)}</span>
              ${sourceCount > 1 ? '<span class="badge badge-sources" title="Covered by ' + sourceCount + ' sources">' + sourceCount + ' sources</span>' : ''}
            </div>
            <span class="story-title">${escapeHtml(story.headline)}</span>
            <span class="story-subtitle">${escapeHtml(getSubtitle(story.summary))}</span>
          </div>
        </div>
        <div class="story-body">
          <p class="story-summary">${escapeHtml(story.summary)}</p>
          <div class="story-sources">
            <h4>Sources</h4>
            <div class="source-list">
              ${(() => {
                const seen = new Set();
                return story.articles.filter(a => {
                  if (seen.has(a.source)) return false;
                  seen.add(a.source);
                  return true;
                }).slice(0, shouldShowDeepDive ? 3 : 10).map(article => 
                  '<a href="' + escapeHtml(article.url) + '" target="_blank" rel="noopener" class="source-link" onclick="event.stopPropagation()">' +
                  '<img src="https://www.google.com/s2/favicons?domain=' + encodeURIComponent(article.sourceUrl) + '&sz=32" class="source-favicon" alt="" onerror="this.style.display=\'none\'">' +
                  escapeHtml(article.source) +
                  '</a>'
                ).join('');
              })()}
            </div>
            ${shouldShowDeepDive ? '<button class="deep-dive-btn" onclick="event.stopPropagation(); window.appShowDeepDive(\'' + story.id + '\')">📖 See all ' + sourceCount + ' sources</button>' : ''}
          </div>
        </div>
      </div>
      <div class="story-actions-sidebar">
        <button class="action-btn-side like" onclick="window.appHandleFeedback('${story.id}', 'like', event)" title="More like this">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </button>
        <button class="action-btn-side dislike" onclick="window.appHandleFeedback('${story.id}', 'dislike', event)" title="Less like this">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"></line></svg>
        </button>
        <button class="action-btn-side bookmark ${bookmarked ? 'active' : ''}" onclick="window.appToggleBookmark('${story.id}', event)" data-bookmark="${story.id}" title="Save for later">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="${bookmarked ? 'currentColor' : 'none'}" stroke="currentColor" stroke-width="2"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
        </button>
        <button class="action-btn-side block" onclick="window.appOpenBlockModal('${story.id}', event)" title="Never show">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
        </button>
        <button class="action-btn-side share" onclick="window.appShareStory('${story.id}', event)" title="Share story">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"></circle><circle cx="6" cy="12" r="3"></circle><circle cx="18" cy="19" r="3"></circle><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line></svg>
        </button>
      </div>
    </article>`;
  }).join('');

  if (lastUpdated) {
    const date = new Date(lastUpdated);
    const timeStr = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    const tzAbbr = date.toLocaleTimeString('en-US', { timeZoneName: 'short' }).split(' ').pop();
    const storyCount = stories.length;
    
    const mainText = storyCount >= 9 
      ? "That's your Top 9. Updated regularly."
      : `Slow news day — just ${storyCount} ${storyCount === 1 ? 'story' : 'stories'} that matter.`;
    
    footerMessage.innerHTML = `${mainText} <span class="update-time">Updated as of ${timeStr} ${tzAbbr}</span>`;
  }
  footerMessage.style.display = 'block';

  if (expandedStoryId) {
    setTimeout(() => {
      const expandedCard = document.querySelector('.story-card.expanded');
      if (expandedCard) expandedCard.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  }
}

function recordStoryRead(storyId) {
  const story = getStories().find(s => s.id === storyId);
  if (!story) { console.log("Story not found for id:", storyId); return; }
  
  fetch(`${API_BASE}/history`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      storyId: story.id, headline: story.headline, category: currentCategory,
      topic: story._meta?.topic, sources: story._meta?.sources
    })
  }).catch(err => console.warn('Failed to record read:', err));
}

export function toggleStory(storyId) {
  const wasExpanded = expandedStoryId === storyId;
  setExpandedStoryId(wasExpanded ? null : storyId);
  
  if (!wasExpanded && expandedStoryId) {
    recordStoryRead(storyId);
    setSessionExpandCount(sessionExpandCount + 1);
    
    const shouldNudge = !nudgeDismissed && !isSignedIn && !hasGivenFeedback &&
      (sessionExpandCount === 3 || (sessionExpandCount > 3 && (sessionExpandCount - 3) % 5 === 0));
    
    if (shouldNudge) showFeedbackNudge();
  }
  renderStories();
}

// Deep dive modal with proper cleanup
export function showDeepDive(storyId) {
  const story = getStories().find(s => s.id === storyId);
  if (!story) return;
  
  // Remove any existing modal first
  const existingModal = document.getElementById('deep-dive-modal');
  if (existingModal) existingModal.remove();
  
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay active';
  overlay.id = 'deep-dive-modal';
  
  const modal = document.createElement('div');
  modal.className = 'modal modal-lg';
  
  // Close modal function
  const closeModal = () => {
    overlay.remove();
    document.removeEventListener('keydown', escHandler);
  };
  
  // Close on Escape key
  const escHandler = (e) => {
    if (e.key === 'Escape') closeModal();
  };
  document.addEventListener('keydown', escHandler);
  
  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeModal();
  });
  
  modal.innerHTML = `
    <div class="modal-content">
      <div class="modal-header">
        <h2>All Sources</h2>
        <button class="modal-close" aria-label="Close">×</button>
      </div>
      <div class="modal-body">
        <h3>${escapeHtml(story.headline)}</h3>
        <div class="deep-dive-list">
          ${story.articles.map(article => `
            <div class="deep-dive-article">
              <img src="https://www.google.com/s2/favicons?domain=${encodeURIComponent(article.sourceUrl)}&sz=32" class="source-favicon" alt="" loading="lazy">
              <div class="deep-dive-article-content">
                <a href="${escapeHtml(article.url)}" target="_blank" rel="noopener" class="deep-dive-article-title">${escapeHtml(article.title)}</a>
                <div class="deep-dive-article-meta">${escapeHtml(article.source)} · ${formatRelativeTime(article.publishedAt)}</div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    </div>
  `;
  
  // Attach close handler to button
  modal.querySelector('.modal-close').addEventListener('click', closeModal);
  
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  
  // Close on overlay click
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) closeModal();
  });
}

window.appShowDeepDive = showDeepDive;

// Extract first sentence for subtitle (max 150 chars)
function getSubtitle(summary) {
  if (!summary) return '';
  const idx = summary.indexOf('.');
  if (idx > 0 && idx < 150) return summary.substring(0, idx + 1);
  return summary.substring(0, 100) + (summary.length > 100 ? '...' : '');
}
